import * as React from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'

import { EmptySearchResult } from '@/components'
import { Table } from '@/components/Table'
import { Input } from '@/components/ui'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { SortOrder } from '@/types/common'
import { SortDirection, TABLE_PAGE_SIZE } from '@/utils/constants'

import { DetailDrawerType, ViewLevel } from '../../constants'
import { useGetApprovedFiles } from '../../hooks'
import { useOrgLevelMetric } from '../../hooks/useOrgLevelMetric'
import { useProjectLevelMetric } from '../../hooks/useProjectLevelMetric'
import { useDashboardStore } from '../../store'
import { TDetailDrawerData } from '../../types'

import {
  useApprovedFilesColumns,
  useAvgReviewTimeColumns,
  useCommentDensityColumns,
  useCommentDensityColumnsForDeliverableLevel,
  useCommentDensityColumnsWithDeliverableSet,
  useLateDeliverablesColumns,
  useLateDeliverablesColumnsForProjectLevel,
  useNoOfProjectsColumns,
} from './columns'

const DetailDrawer: React.FC = () => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = React.useState<string>('')
  const [orderBy, setOrderBy] = React.useState<{
    column: string
    direction: SortOrder
  }>({ column: 'project_name', direction: null })
  const [page, setPage] = React.useState<number>(0)

  const {
    filters,
    displayedDrawer,
    setDisplayedDrawer,
    viewLevel,
    aggregationPeriod,
    timeRange,
  } = useDashboardStore()

  const noOfProjectsColumns = useNoOfProjectsColumns()
  const lateDeliverablesColumns = useLateDeliverablesColumns()
  const lateDeliverablesColumnsForProjectLevel =
    useLateDeliverablesColumnsForProjectLevel()
  const commentDensityColumns = useCommentDensityColumns()
  const commentDensityColumnsWithDeliverableSet =
    useCommentDensityColumnsWithDeliverableSet()
  const commentDensityColumnsForDeliverableLevel =
    useCommentDensityColumnsForDeliverableLevel()
  const avgReviewTimeColumns = useAvgReviewTimeColumns()
  const approvedFilesColumns = useApprovedFilesColumns(aggregationPeriod)

  const {
    projectMetric: { data: projectMetric } = {},
    lateDeliverablesMetric: { data: orgLateDeliverablesMetric } = {},
    commentDensityMetric: { data: orgCommentDensityMetric } = {},
  } = useOrgLevelMetric()

  const {
    lateDeliverablesMetric: { data: projectLateDeliverablesMetric } = {},
    commentDensityMetric: { data: projectCommentDensityMetric } = {},
    avgCycleTimeMetric: { data: projectAvgCycleTimeMetric } = {},
  } = useProjectLevelMetric()

  const { data: { data: [approvedFilesResponse] = [] } = {} } =
    useGetApprovedFiles({
      bucket_type: aggregationPeriod,
      time_range: timeRange,
      company_id: filters.companyId ?? undefined,
    })

  const approvedFilesData = approvedFilesResponse?.data

  // Check if the drawer is open
  const isOpen = useMemo(() => !!displayedDrawer, [displayedDrawer])

  // Determine which data to use based on view level
  const lateDeliverablesMetric = useMemo(() => {
    return viewLevel === ViewLevel.PROJECTS
      ? projectLateDeliverablesMetric
      : orgLateDeliverablesMetric
  }, [viewLevel, projectLateDeliverablesMetric, orgLateDeliverablesMetric])

  const commentDensityMetric = useMemo(() => {
    return viewLevel === ViewLevel.PROJECTS
      ? projectCommentDensityMetric
      : orgCommentDensityMetric
  }, [viewLevel, projectCommentDensityMetric, orgCommentDensityMetric])

  // Get the data for the drawer
  const {
    title = '',
    data = [],
    columns,
  } = useMemo<TDetailDrawerData>(() => {
    // If the drawer is not open, return an empty array
    if (!isOpen) return { title: '', data: [], columns: [] }

    switch (displayedDrawer) {
      case DetailDrawerType.NO_OF_PROJECTS:
        return {
          data: projectMetric,
          title: t('dashboard.metrics.noOfProjects'),
          columns: noOfProjectsColumns,
        }

      case DetailDrawerType.LATE_DELIVERABLES: {
        const isProjectLevel = viewLevel === ViewLevel.PROJECTS
        return {
          data: lateDeliverablesMetric,
          title: t('dashboard.metrics.lateDeliverables'),
          columns: isProjectLevel
            ? lateDeliverablesColumnsForProjectLevel
            : lateDeliverablesColumns,
        }
      }

      case DetailDrawerType.COMMENT_DENSITY: {
        let columns = commentDensityColumns
        if (filters.deliverableSetId) {
          columns = commentDensityColumnsForDeliverableLevel
        } else if (filters.projectId) {
          columns = commentDensityColumnsWithDeliverableSet
        }
        return {
          data: commentDensityMetric,
          title: t('dashboard.metrics.commentDensity'),
          columns,
        }
      }

      case DetailDrawerType.AVG_REVIEW_TIME:
        return {
          data: projectAvgCycleTimeMetric,
          title: t('dashboard.metrics.avgReview'),
          columns: avgReviewTimeColumns,
        }

      case DetailDrawerType.TOTAL_OUTPUTS_APPROVED:
        return {
          data: approvedFilesData,
          title: t('dashboard.periodMetrics.totalOutputsApproved'),
          columns: approvedFilesColumns,
        }

      default:
        return { title: '', data: [], columns: [] }
    }
  }, [
    commentDensityColumns,
    commentDensityColumnsWithDeliverableSet,
    commentDensityColumnsForDeliverableLevel,
    commentDensityMetric,
    displayedDrawer,
    filters.projectId,
    filters.deliverableSetId,
    isOpen,
    lateDeliverablesColumns,
    lateDeliverablesColumnsForProjectLevel,
    lateDeliverablesMetric,
    noOfProjectsColumns,
    projectMetric,
    t,
    viewLevel,
    avgReviewTimeColumns,
    projectAvgCycleTimeMetric,
    approvedFilesData,
    approvedFilesColumns,
  ])

  // Filter the data for the drawer
  const filteredData = useMemo(() => {
    return data
      .filter((item) => {
        // If no search text, return all items
        if (!searchText) return true

        // If the item has a project_name, check if the project_name includes the search text
        if ('project_name' in item) {
          return item?.project_name
            ?.toLowerCase()
            .includes(searchText.toLowerCase())
        }

        // If the item has a ds_name, check if the ds_name includes the search text
        if ('ds_name' in item) {
          return item?.ds_name?.toLowerCase().includes(searchText.toLowerCase())
        }

        // If the item has a deliverable_set_name, check if the deliverable_set_name includes the search text
        if ('deliverable_set_name' in item) {
          return item?.deliverable_set_name
            ?.toLowerCase()
            .includes(searchText.toLowerCase())
        }

        return false
      })
      .sort((a, b) => {
        const valA = a[orderBy.column as keyof typeof a]
        const valB = b[orderBy.column as keyof typeof b]

        if (typeof valA === 'string' && typeof valB === 'string') {
          // String comparison
          if (orderBy.direction === SortDirection.ASC) {
            return (valA as string).localeCompare(valB)
          } else {
            return (valB as string).localeCompare(valA)
          }
        } else if (typeof valA === 'number' && typeof valB === 'number') {
          // Number comparison
          if (orderBy.direction === SortDirection.ASC) {
            return valA - valB
          } else {
            return valB - valA
          }
        } else {
          // Fallback if types don't match or are not string/number
          return 0
        }
      })
  }, [data, orderBy.column, orderBy.direction, searchText])

  const setDefaultOrderBy = React.useCallback(() => {
    switch (displayedDrawer) {
      case DetailDrawerType.NO_OF_PROJECTS:
        setOrderBy({
          column: 'active_deliverables',
          direction: SortDirection.DESC,
        })
        break
      case DetailDrawerType.LATE_DELIVERABLES:
        setOrderBy({ column: 'overdue_days', direction: SortDirection.DESC })
        break
      case DetailDrawerType.COMMENT_DENSITY:
        setOrderBy({ column: 'comment_density', direction: SortDirection.DESC })
        break
      case DetailDrawerType.AVG_REVIEW_TIME:
        setOrderBy({ column: 'cycle_time_days', direction: SortDirection.DESC })
        break

      case DetailDrawerType.TOTAL_OUTPUTS_APPROVED:
        setOrderBy({ column: 'bucket_date', direction: SortDirection.ASC })
        break

      default:
        break
    }
  }, [displayedDrawer])

  React.useEffect(() => {
    if (!isOpen) return

    setDefaultOrderBy()
  }, [isOpen, setDefaultOrderBy])

  // Reset page to 0 when search text changes
  React.useEffect(() => {
    if (!isOpen) setPage(0)
  }, [isOpen])

  const noDataMessage = useMemo(() => {
    if (searchText) {
      return <EmptySearchResult translationKey="deliverableSet" />
    }
    return null
  }, [searchText])

  const searchPlaceholder = useMemo(() => {
    // At project level, comment density shows deliverable sets
    if (
      displayedDrawer === DetailDrawerType.COMMENT_DENSITY &&
      filters.projectId
    ) {
      return t('dashboard.drawer.searchDeliverableSet')
    }
    // At project level, avg review time shows deliverable sets
    if (displayedDrawer === DetailDrawerType.AVG_REVIEW_TIME) {
      return t('dashboard.drawer.searchDeliverableSet')
    }
    // At project level, late deliverables shows deliverable sets
    if (
      displayedDrawer === DetailDrawerType.LATE_DELIVERABLES &&
      viewLevel === ViewLevel.PROJECTS
    ) {
      return t('dashboard.drawer.searchDeliverableSet')
    }
    // All other cases show projects
    return t('dashboard.drawer.searchProject')
  }, [displayedDrawer, filters.projectId, viewLevel, t])

  // Determine if search should be shown
  const showSearch = useMemo(() => {
    const isTotalOutputsApproved =
      displayedDrawer === DetailDrawerType.TOTAL_OUTPUTS_APPROVED

    const isCommentDensityAtDeliverable =
      displayedDrawer === DetailDrawerType.COMMENT_DENSITY &&
      Boolean(filters.deliverableSetId)

    return !(isTotalOutputsApproved || isCommentDensityAtDeliverable)
  }, [displayedDrawer, filters.deliverableSetId])

  const handleOpenChange = React.useCallback(() => {
    // Reset the search text
    setSearchText('')

    // Reset the displayed drawer
    setDisplayedDrawer(null)
  }, [setDisplayedDrawer])

  const handleSortChange = (colId: null, dir: SortOrder) => {
    if (!colId || !dir) {
      setDefaultOrderBy()
      return
    }
    setOrderBy({ column: colId as unknown as string, direction: dir })
  }

  // Get the subtitle for TOTAL_OUTPUTS_APPROVED drawer
  const subtitle = useMemo(() => {
    if (displayedDrawer === DetailDrawerType.TOTAL_OUTPUTS_APPROVED) {
      return t(`dashboard.timeRanges.${timeRange}`)
    }
    return null
  }, [displayedDrawer, timeRange, t])

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange} direction="right">
      <DrawerContent className="!max-w-[90dvw] !w-fit" data-vaul-no-drag>
        <DrawerHeader className="pb-0">
          <DrawerTitle>{title}</DrawerTitle>
          {subtitle && (
            <DrawerDescription className="!mt-1 text-tertiary-600">
              {subtitle}
            </DrawerDescription>
          )}
          {!subtitle && <DrawerDescription hidden />}
        </DrawerHeader>
        <div className="px-6 pb-8 pt-6 flex flex-col gap-4 flex-1 overflow-hidden">
          {showSearch && (
            <div className="relative w-60 flex-shrink-0">
              <Input
                type="text"
                leadingIcon={<Search size={20} className="text-gray-400" />}
                placeholder={searchPlaceholder}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full"
                autoComplete="off"
                trailingIcon={
                  searchText ? (
                    <X className="size-5 cursor-pointer" />
                  ) : undefined
                }
                onTrailingIconClick={
                  searchText ? () => setSearchText('') : undefined
                }
              />
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-hidden">
            {columns ? (
              <Table
                fullHeight
                roundedTop
                className="h-full flex flex-col"
                columns={columns}
                data={
                  filteredData.slice(
                    page * TABLE_PAGE_SIZE,
                    (page + 1) * TABLE_PAGE_SIZE
                  ) ?? []
                }
                page={page}
                totalPages={Math.ceil(filteredData.length / TABLE_PAGE_SIZE)}
                onPageChange={setPage}
                noDataMessage={noDataMessage}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                orderBy={orderBy.column as any}
                order={orderBy.direction}
                onSortChange={handleSortChange}
                onRowClick={() => {}}
              />
            ) : null}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

const DetailDrawerMemo = React.memo(DetailDrawer)
DetailDrawerMemo.displayName = 'DetailDrawer'
export { DetailDrawerMemo as DetailDrawer }
